import { clearSupabaseCache } from "../pwaUtils";
import { logger } from "@/utils/logger";

// Define the CachedData interface at the top of the file
interface CachedData<T> {
  data: T;
  timestamp: number;
}

// TTL values for different cache types (in milliseconds)
export const CACHE_TTL = {
  SHORT: 2 * 60 * 1000,        // 2 minutes (increased from 1 minute)
  MEDIUM: 10 * 60 * 1000,      // 10 minutes (increased from 5 minutes)
  LONG: 60 * 60 * 1000,        // 60 minutes (increased from 30 minutes)
  VERY_LONG: 4 * 60 * 60 * 1000 // 4 hours (increased from 1 hour)
};

// Cache key prefixes for different data types
export const CACHE_KEYS = {
  CUSTOM_FIELDS: 'custom_fields_',
  PAYMENTS: 'payments_',
  COLLABORATORS: 'collaborator_emails_',
  PATIENTS: 'optimized_patients_',
  QUERY_RESULTS: 'query_results_',
};

// Memory cache for high-frequency requests
const memoryCache = new Map<string, {
  data: any,
  timestamp: number
}>();

// Maximum items to store in memory cache to prevent excessive memory usage
const MAX_MEMORY_CACHE_SIZE = 200; // Increased from 100 to 200 for better performance

// Estimated maximum size for sessionStorage items (in bytes)
// This is a conservative estimate to avoid quota issues
const MAX_STORAGE_ITEM_SIZE = 2 * 1024 * 1024; // 2MB

/**
 * Set data in session storage with TTL and memory cache
 * @param key Cache key
 * @param data Data to cache
 * @param ttl Time to live in milliseconds
 * @param useMemory Whether to also cache in memory
 */
export function setCache<T>(key: string, data: T, ttl: number = CACHE_TTL.MEDIUM, useMemory: boolean = true): boolean {
  try {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    
    // Check if we're close to storage quota before attempting to write
    try {
      // Check if we're in logout process - if so, don't attempt to write
      if (sessionStorage.getItem('logout_in_progress') === 'true') {
        logger.info("Logout in progress, skipping cache write for:", key);
        return false;
      }
    } catch (e) {
      // Ignore errors here
    }
    
    const cacheEntry: CachedData<T> = {
      data,
      timestamp: Date.now()
    };
    
    // Store in memory cache for faster access
    if (useMemory) {
      memoryCache.set(key, cacheEntry);
      
      // Prevent memory cache from growing too large
      if (memoryCache.size > MAX_MEMORY_CACHE_SIZE) {
        // Find the oldest 20 items to remove
        const entries = Array.from(memoryCache.entries());
        const oldestEntries = entries
          .sort((a, b) => a[1].timestamp - b[1].timestamp)
          .slice(0, 20);
        
        // Delete oldest items
        for (const [oldKey] of oldestEntries) {
          memoryCache.delete(oldKey);
        }
      }
    }
    
    // Attempt to store in session storage with size check
    const dataString = JSON.stringify(cacheEntry);
    
    // Skip storage if data is too large
    if (dataString.length > MAX_STORAGE_ITEM_SIZE) {
      logger.warn(`Cache data for ${key} exceeds size limit (${(dataString.length / 1024 / 1024).toFixed(2)}MB), skipping session storage`);
      return useMemory; // Return true if it was at least stored in memory
    }
    
    try {
      // Try to clear some space before writing
      if (key.startsWith(CACHE_KEYS.PATIENTS) || dataString.length > MAX_STORAGE_ITEM_SIZE / 2) {
        // Remove older items for the same data type to make space
        const keyPrefix = key.split('_')[0];
        const keysToRemove = [];
        
        for (let i = 0; i < sessionStorage.length; i++) {
          const storageKey = sessionStorage.key(i);
          if (storageKey && storageKey.startsWith(keyPrefix) && storageKey !== key) {
            keysToRemove.push(storageKey);
            if (keysToRemove.length >= 5) break; // Limit to removing 5 keys at once
          }
        }
        
        // Remove found items
        for (const keyToRemove of keysToRemove) {
          logger.info(`Removing ${keyToRemove} to make space for ${key}`);
          sessionStorage.removeItem(keyToRemove);
        }
      }
      
      // Store in session storage
      sessionStorage.setItem(key, dataString);
      return true;
    } catch (storageError) {
      logger.warn(`Failed to store in sessionStorage: ${storageError.message}`);
      
      // If it's a quota error, aggressively clear cache
      if (storageError.name === 'QuotaExceededError') {
        logger.info("Storage quota exceeded, performing emergency cleanup");
        
        try {
          // Clear all items for this prefix first
          const keyPrefix = key.split('_')[0];
          const keysWithSamePrefix = [];
          
          for (let i = 0; i < sessionStorage.length; i++) {
            const storageKey = sessionStorage.key(i);
            if (storageKey && storageKey.startsWith(keyPrefix)) {
              keysWithSamePrefix.push(storageKey);
            }
          }
          
          // Delete all keys with same prefix
          keysWithSamePrefix.forEach(k => {
            sessionStorage.removeItem(k);
            logger.info(`Emergency cleanup: removed ${k}`);
          });
          
          // If still not enough space, remove more items
          if (keysWithSamePrefix.length === 0 || key.includes('list')) {
            // Get all keys
            const allKeys = [];
            for (let i = 0; i < sessionStorage.length; i++) {
              const storageKey = sessionStorage.key(i);
              if (storageKey) {
                allKeys.push(storageKey);
              }
            }
            
            // Remove half of all keys
            const keysToRemove = allKeys.slice(0, Math.floor(allKeys.length / 2));
            keysToRemove.forEach(k => {
              sessionStorage.removeItem(k);
              logger.info(`Deep cleanup: removed ${k}`);
            });
          }
          
          // Try one more time after cleanup
          try {
            sessionStorage.setItem(key, dataString);
            logger.info("Successfully stored after aggressive cleanup");
            return true;
          } catch (retryError) {
            logger.error("Failed to store even after aggressive cleanup", retryError);
          }
        } catch (cleanupError) {
          logger.error("Error during emergency cleanup:", cleanupError);
        }
      }
      
      // Return true if we at least stored it in memory
      return useMemory;
    }
  } catch (e) {
    logger.error("Error setting cache:", e);
    
    // Try memory cache as fallback
    if (useMemory) {
      try {
        memoryCache.set(key, {
          data,
          timestamp: Date.now()
        });
        return true;
      } catch (memError) {
        logger.error("Error setting memory cache:", memError);
      }
    }
    
    return false;
  }
}

/**
 * Get data from cache with TTL check (checks memory first, then session storage)
 * @param key Cache key
 * @param ttl Time to live in milliseconds
 * @returns Cached data or null if expired/not found
 */
export function getCache<T>(key: string, ttl: number = CACHE_TTL.MEDIUM): T | null {
  try {
    const now = Date.now();
    
    // Check memory cache first for faster access
    if (memoryCache.has(key)) {
      const memoryCacheEntry = memoryCache.get(key);
      
      // Check if memory cache is valid
      if (memoryCacheEntry && now - memoryCacheEntry.timestamp <= ttl) {
        return memoryCacheEntry.data as T;
      }
      
      // Remove expired memory cache
      memoryCache.delete(key);
    }
    
    // If not in memory or expired, check session storage
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    
    const cached = sessionStorage.getItem(key);
    if (!cached) {
      return null;
    }
    
    const cacheEntry = JSON.parse(cached) as CachedData<T>;
    
    // Check if cache is expired
    if (now - cacheEntry.timestamp > ttl) {
      // For debugging only log if it's been a significant time
      if (now - cacheEntry.timestamp > ttl + 5000) {
        logger.info(`Cache expired for ${key} (age: ${(now - cacheEntry.timestamp) / 1000}s, max: ${ttl / 1000}s)`);
      }
      return null;
    }
    
    // Add to memory cache for faster access next time
    memoryCache.set(key, cacheEntry);
    
    return cacheEntry.data;
  } catch (e) {
    logger.error("Error getting cache:", e);
    return null;
  }
}

/**
 * Check if a cache entry exists and is valid
 * @param key Cache key 
 * @param ttl Time to live in milliseconds
 * @returns True if valid cache entry exists
 */
export function hasCacheValid(key: string, ttl: number = CACHE_TTL.MEDIUM): boolean {
  try {
    const now = Date.now();
    
    // Check memory cache first
    if (memoryCache.has(key)) {
      const memoryCacheEntry = memoryCache.get(key);
      if (memoryCacheEntry && now - memoryCacheEntry.timestamp <= ttl) {
        return true;
      }
      memoryCache.delete(key);
    }
    
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    
    const cached = sessionStorage.getItem(key);
    if (!cached) {
      return false;
    }
    
    const cacheEntry = JSON.parse(cached) as CachedData<any>;
    return (now - cacheEntry.timestamp <= ttl);
  } catch (e) {
    logger.error("Error checking cache validity:", e);
    return false;
  }
}

/**
 * Clear a specific cache entry
 * @param key Cache key to clear
 * @returns True if operation was successful
 */
export function clearCache(key: string): boolean {
  try {
    // Clear from memory cache
    memoryCache.delete(key);
    
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    
    sessionStorage.removeItem(key);
    return true;
  } catch (e) {
    logger.error("Error clearing cache:", e);
    return false;
  }
}

/**
 * Clear all application caches
 * This is useful when encountering issues with stale data or excessive API requests
 */
export async function clearAllCaches(): Promise<void> {
  logger.info("Clearing all application caches");
  
  // Clear memory cache
  memoryCache.clear();
  
  // Clear session storage
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.clear();
      logger.info("Session storage cleared");
    }
  } catch (e) {
    logger.error("Error clearing session storage:", e);
  }
  
  // Clear local storage
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('supabase.auth.expires_at');
      localStorage.removeItem('supabase.auth.refresh_token');
      logger.info("Auth storage items cleared");
    }
  } catch (e) {
    logger.error("Error clearing localStorage items:", e);
  }
  
  // Clear service worker cache for Supabase requests
  try {
    await clearSupabaseCache();
    logger.info("Service worker cache cleared for Supabase requests");
  } catch (e) {
    logger.error("Error clearing service worker cache:", e);
  }
  
  // Broadcast cache clear event for components to react
  try {
    window.dispatchEvent(new CustomEvent('app:cache-cleared'));
    logger.info("Cache clear event dispatched");
  } catch (e) {
    logger.error("Error dispatching cache clear event:", e);
  }
}

/**
 * Clear caches related to a specific patient
 * @param patientId ID of the patient
 */
export function clearPatientCache(patientId: string): void {
  try {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    
    // Look for patient-related caches
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (
        key.includes(patientId) ||
        key.startsWith(CACHE_KEYS.PATIENTS) ||
        key.startsWith(CACHE_KEYS.CUSTOM_FIELDS) ||
        key.startsWith(CACHE_KEYS.PAYMENTS)
      )) {
        sessionStorage.removeItem(key);
        logger.info(`Cleared cache for key: ${key}`);
      }
    }
    
    logger.info(`All caches cleared for patient ${patientId}`);
  } catch (e) {
    logger.error("Error clearing patient caches:", e);
  }
}

/**
 * Clear all caches of a specific type
 * @param keyPrefix Prefix of the cache keys to clear
 */
export function clearCachesByPrefix(keyPrefix: string): void {
  try {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    
    const keysToRemove = [];
    
    // Find all keys matching the prefix
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(keyPrefix)) {
        keysToRemove.push(key);
      }
    }
    
    // Remove all matching keys
    keysToRemove.forEach(key => {
      sessionStorage.removeItem(key);
    });
    
    logger.info(`Cleared ${keysToRemove.length} caches with prefix '${keyPrefix}'`);
  } catch (e) {
    logger.error(`Error clearing caches with prefix '${keyPrefix}':`, e);
  }
}

/**
 * Get cache statistics for debugging
 */
export function getCacheStats(): Record<string, any> {
  const stats: Record<string, any> = {
    memoryCache: {
      itemCount: memoryCache.size,
      keys: Array.from(memoryCache.keys())
    },
    sessionStorage: {
      itemCount: 0,
      keys: [],
      totalSize: 0,
      byPrefix: {}
    }
  };
  
  // Get session storage stats
  try {
    if (typeof sessionStorage !== 'undefined') {
      stats.sessionStorage.itemCount = sessionStorage.length;
      stats.sessionStorage.keys = [];
      let totalSize = 0;
      
      // Initialize prefix counters
      Object.values(CACHE_KEYS).forEach(prefix => {
        stats.sessionStorage.byPrefix[prefix] = {
          count: 0,
          size: 0
        };
      });
      
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key) {
          const value = sessionStorage.getItem(key) || '';
          stats.sessionStorage.keys.push(key);
          const itemSize = key.length + value.length;
          totalSize += itemSize;
          
          // Count by prefix
          for (const [prefixName, prefix] of Object.entries(CACHE_KEYS)) {
            if (key.startsWith(prefix)) {
              stats.sessionStorage.byPrefix[prefix].count++;
              stats.sessionStorage.byPrefix[prefix].size += itemSize;
              break;
            }
          }
        }
      }
      
      stats.sessionStorage.totalSize = `${Math.round(totalSize / 1024)} KB`;
      
      // Convert byte sizes to KB for prefix stats
      for (const prefix of Object.values(CACHE_KEYS)) {
        if (stats.sessionStorage.byPrefix[prefix]) {
          stats.sessionStorage.byPrefix[prefix].size = 
            `${Math.round(stats.sessionStorage.byPrefix[prefix].size / 1024)} KB`;
        }
      }
    }
  } catch (e) {
    logger.error("Error getting session storage stats:", e);
  }
  
  return stats;
}
